<script lang="ts" context="module">
    export enum HighlighterModalState {
        AUTHORIZING = "authorizing",
        LOGIN = "login",
        LOADING = "loading",
        LOADED = "loaded",
    }
</script>

<script lang="ts">
    import { createEventDispatcher, onMount } from "svelte";
    import { TokenStore } from "$/stores/TokenStore.js";
    import { extensionInit } from "$/stores/StoreInitializer";
    import { fade, slide } from "svelte/transition";
    import LoginPanel from "../LoginPanel.svelte";
    import HighlighterCard from "./HighlighterCard.svelte";
    import HighlighterMessage from "./HighlighterMessage.svelte";
    import { ClusterStore, ClusterType } from "$/stores/ClusterStore";
    import { SelectionUtils } from "$/utils/SelectionUtils";
    import { CardTitleUtils } from "../card/CardTitle/CardTitleUtils";
    import { sanitizeHTMLCardContent, cleanUrl } from "$/utils/Utils";
    import { TopicAccessLevel } from "$/stores/extension/ActiveTopicStore";

    export let location: Location = window.location;
    export let highlight = null;
    const dispatch = createEventDispatcher();

    let state: HighlighterModalState = HighlighterModalState.AUTHORIZING;
    let isSelectionEmpty =
        window.getSelection().type === "None" ||
        window.getSelection().toString().trim() === "";
    let suggestions;

    const closeHighlighterModal = (event) => {
        dispatch("close");
    };

    async function loadData() {
        state = HighlighterModalState.LOADING;

        await extensionInit(TopicAccessLevel.EDITOR);

        ClusterStore.removeClustersWithTopicsWithoutEditRole($TokenStore?.user.id);
        state = HighlighterModalState.LOADED;
    }

    function generateContent(): string {
        // Prioritize highlight.content if available and not empty
        if (highlight?.content && highlight.content.trim()) {
            return sanitizeHTMLCardContent(highlight.content);
        }
        // If no highlight content but there is a selection, use the selection
        if (!isSelectionEmpty) {
            return sanitizeHTMLCardContent(
                SelectionUtils.getCleanContentFromSelection(
                    document.getSelection(),
                    document,
                    location,
                ),
            );
        }
        // Fallback to meta description if no selection and no highlight content
        return SelectionUtils.getContentFromDocumentDescription(document);
    }

    function generateUrl() {
        if (isSelectionEmpty) {
            return cleanUrl(location.href);
        }
        return SelectionUtils.getUrlFromSelection(document, location);
    }

    onMount(async () => {
        await TokenStore.init();

        if ($TokenStore.expired) {
            state = HighlighterModalState.LOGIN;
            return;
        }

        await loadData();
    });
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
    class="highlighter-modal__backdrop js-cw-highlighter-modal-backdrop"
    transition:fade
    on:mousedown|self={closeHighlighterModal}
    on:keydown|stopPropagation
    on:keyup|stopPropagation
    on:keypress|stopPropagation
>
    <div class="highlighter-modal" transition:slide>
        {#if state === HighlighterModalState.AUTHORIZING}
            <HighlighterMessage>Authorizing...</HighlighterMessage>
        {:else if state === HighlighterModalState.LOGIN}
            <div class="highlighter-modal__content">
                <LoginPanel afterLogin={loadData} />
            </div>
        {:else if state === HighlighterModalState.LOADING}
            <HighlighterMessage>Loading...</HighlighterMessage>
        {:else if state === HighlighterModalState.LOADED}
            <HighlighterCard
                on:close
                on:create
                content={generateContent()}
                url={generateUrl()}
                title={CardTitleUtils.generateTitle(document)}
                bookmark={isSelectionEmpty}
                {highlight}
            />
        {/if}
    </div>
</div>

<style>
    .highlighter-modal__backdrop {
        @apply fixed inset-0 z-max flex flex-wrap items-center justify-center overflow-auto bg-black bg-opacity-0;
    }

    .highlighter-modal {
        @apply relative z-10 m-4 mx-auto h-auto w-[360px] rounded-xl bg-white shadow-2xl;
    }

    .highlighter-modal__content {
        @apply rounded-xl overflow-clip;
    }
</style>
